import { useCallback, useEffect, useState } from 'react';
import { getPublicCms } from '@/api/cms';

export interface PublicSubPageContent {
  solutionDetail: {
    helpCard: {
      title: string;
      desc: string;
      btnText: string;
    };
    relatedTitle: string;
    relatedSubtitle: string;
    relatedAltTitle: string;
    relatedAltSubtitle: string;
  };
  toolDetail: {
    headerGradientFrom: string;
    headerGradientTo: string;
    helpCard: {
      title: string;
      desc: string;
      btnText: string;
    };
  };
  about: {
    header: {
      badge: string;
      title: string;
      subtitle: string;
    };
    story: {
      title: string;
      para1: string;
      para2: string;
      para3: string;
    };
    values: {
      title: string;
      item1Title: string;
      item1Desc: string;
      item2Title: string;
      item2Desc: string;
      item3Title: string;
      item3Desc: string;
    };
  };
  solutionList: {
    badge: string;
    title: string;
    subtitle: string;
  };
  toolList: {
    badge: string;
    title: string;
    subtitle: string;
  };
}

const EMPTY_SUB_PAGE_CONTENT: PublicSubPageContent = {
  solutionDetail: {
    helpCard: {
      title: '',
      desc: '',
      btnText: '',
    },
    relatedTitle: '',
    relatedSubtitle: '',
    relatedAltTitle: '',
    relatedAltSubtitle: '',
  },
  toolDetail: {
    headerGradientFrom: '',
    headerGradientTo: '',
    helpCard: {
      title: '',
      desc: '',
      btnText: '',
    },
  },
  about: {
    header: {
      badge: '',
      title: '',
      subtitle: '',
    },
    story: {
      title: '',
      para1: '',
      para2: '',
      para3: '',
    },
    values: {
      title: '',
      item1Title: '',
      item1Desc: '',
      item2Title: '',
      item2Desc: '',
      item3Title: '',
      item3Desc: '',
    },
  },
  solutionList: {
    badge: '',
    title: '',
    subtitle: '',
  },
  toolList: {
    badge: '',
    title: '',
    subtitle: '',
  },
};

function readText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object') return {};
  return value as Record<string, unknown>;
}

function normalizeSubPageContent(raw: unknown): PublicSubPageContent {
  const root = asRecord(raw);
  const solutionDetail = asRecord(root.solutionDetail);
  const solutionHelpCard = asRecord(solutionDetail.helpCard);
  const toolDetail = asRecord(root.toolDetail);
  const toolHelpCard = asRecord(toolDetail.helpCard);
  const about = asRecord(root.about);
  const aboutHeader = asRecord(about.header);
  const aboutStory = asRecord(about.story);
  const aboutValues = asRecord(about.values);
  const solutionList = asRecord(root.solutionList);
  const toolList = asRecord(root.toolList);

  return {
    solutionDetail: {
      helpCard: {
        title: readText(solutionHelpCard.title),
        desc: readText(solutionHelpCard.desc),
        btnText: readText(solutionHelpCard.btnText),
      },
      relatedTitle: readText(solutionDetail.relatedTitle),
      relatedSubtitle: readText(solutionDetail.relatedSubtitle),
      relatedAltTitle: readText(solutionDetail.relatedAltTitle),
      relatedAltSubtitle: readText(solutionDetail.relatedAltSubtitle),
    },
    toolDetail: {
      headerGradientFrom: readText(toolDetail.headerGradientFrom),
      headerGradientTo: readText(toolDetail.headerGradientTo),
      helpCard: {
        title: readText(toolHelpCard.title),
        desc: readText(toolHelpCard.desc),
        btnText: readText(toolHelpCard.btnText),
      },
    },
    about: {
      header: {
        badge: readText(aboutHeader.badge),
        title: readText(aboutHeader.title),
        subtitle: readText(aboutHeader.subtitle),
      },
      story: {
        title: readText(aboutStory.title),
        para1: readText(aboutStory.para1),
        para2: readText(aboutStory.para2),
        para3: readText(aboutStory.para3),
      },
      values: {
        title: readText(aboutValues.title),
        item1Title: readText(aboutValues.item1Title),
        item1Desc: readText(aboutValues.item1Desc),
        item2Title: readText(aboutValues.item2Title),
        item2Desc: readText(aboutValues.item2Desc),
        item3Title: readText(aboutValues.item3Title),
        item3Desc: readText(aboutValues.item3Desc),
      },
    },
    solutionList: {
      badge: readText(solutionList.badge),
      title: readText(solutionList.title),
      subtitle: readText(solutionList.subtitle),
    },
    toolList: {
      badge: readText(toolList.badge),
      title: readText(toolList.title),
      subtitle: readText(toolList.subtitle),
    },
  };
}

export function usePublicSubPageContent() {
  const [content, setContent] = useState<PublicSubPageContent>(EMPTY_SUB_PAGE_CONTENT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const cms = await getPublicCms('sub_page_content');
      setContent(normalizeSubPageContent(cms));
      setError(null);
    } catch (err) {
      setContent(EMPTY_SUB_PAGE_CONTENT);
      setError(err instanceof Error ? err.message : '二级页面内容加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const handleUpdate = () => void refresh();
    window.addEventListener('wanxiang_subpage_content_updated', handleUpdate);
    return () => window.removeEventListener('wanxiang_subpage_content_updated', handleUpdate);
  }, [refresh]);

  return { content, loading, error, refresh };
}
